import React, { useState, useEffect } from "react";
import {
    Modal,
    Button,
    Form,
    Input,
    Typography,
    Select,
    Alert,
    notification,
    Spin,
} from "antd";
import RateApi from "../../common/Apis/RatesApi";
import { createDefaultTenantRate } from "../../../models/Rate/TenantRateDTO";

const { Title } = Typography;
const { Option } = Select;

const UpdateTenantRateModal = ({ show, onClose, onSubmit, tenant }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [rateId, setRateId] = useState(0);
    const [submittable, setSubmittable] = useState(false);

    // Watch form values
    const values = Form.useWatch([], form);

    // Validate form when values change
    useEffect(() => {
        if (show) {
            form.validateFields({ validateOnly: true })
                .then(() => setSubmittable(true))
                .catch(() => setSubmittable(false));
        }
    }, [values, form, show]);

    // Fetch active tenant rate when modal opens
    useEffect(() => {
        if (show && tenant?.id) {
            setFetching(true);
            setApiError(null);

            RateApi.GetActiveTenantRate(tenant.id)
                .then((data) => {
                    form.resetFields();
                    form.setFieldsValue({
                        paymentTerm: data.payload?.billingCycle || undefined,
                        rate: data.payload?.agreedRate || undefined,
                    });
                    setRateId(data.payload?.id || 0);
                    
                    // Trigger initial validation after setting values
                    setTimeout(() => {
                        form.validateFields({ validateOnly: true })
                            .then(() => setSubmittable(true))
                            .catch(() => setSubmittable(false));
                    }, 0);
                })
                .catch((err) => {
                    setApiError(
                        err?.apiError?.message ||
                        err?.message ||
                        "Failed to load current tenant rate."
                    );
                })
                .finally(() => setFetching(false));
        }
    }, [show, tenant, form]);

    const handleRenew = async () => {
        try {
            const values = await form.validateFields();
            if (!tenant?.id) {
                return notification.error({ message: "Tenant ID is missing." });
            }

            const payload = createDefaultTenantRate({
                id: rateId,
                tenantDTO: { id: tenant?.id },
                billingCycle: values.paymentTerm,
                agreedRate: values.rate,
            });

            setLoading(true);
            await RateApi.UpdateTenantRate(rateId, payload);
            notification.success({
                message: (
                    <span style={{ fontWeight: "bold" }}>
                        Tenant / Customer subscription plan updated successfully!
                    </span>
                ),
                duration: 3,
            });
            setLoading(false);
            setApiError(null);
            onSubmit?.();
            onClose();
        } catch (err) {
            if (err.errorFields) {
                notification.error({
                    message: "Please fix the form errors before proceeding.",
                });
            } else {
                setApiError(
                    err?.apiError?.message ||
                    err?.message ||
                    "Failed to update subscription plan. Please try again."
                );
                setLoading(false);
            }
        }
    };

    return (
        <Modal
            footer={null}
            width={650}
            open={show}
            maskClosable={false}
            onCancel={onClose}
            closable={true}
        >
            <Title level={4} className="text-center mb-3">
                Update Tenant / Customer subscription plan
            </Title>

            {fetching ? (
                <div className="flex justify-center items-center py-8">
                    <Spin size="large" />
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    name="update_tenant_rate_form"
                >
                    <Form.Item
                        name="paymentTerm"
                        label={
                            <span>
                                Payment Term <span style={{ color: "red" }}>*</span>
                            </span>
                        }
                        rules={[
                            { required: true, message: "Please select payment term" },
                        ]}
                    >
                        <Select placeholder="Select Payment Term">
                            <Option value="MONTHLY">Monthly</Option>
                            <Option value="ANNUALLY">Annual</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                Rate <span style={{ color: "red" }}>*</span>
                            </span>
                        }
                        name="rate"
                        rules={[
                            { required: true, message: "Please enter rate" },
                            {
                                validator(_, value) {
                                    if (isNaN(value)) {
                                        return Promise.reject("Rate must be a number");
                                    }
                                    if (Number(value) < 0) {
                                        return Promise.reject("Rate cannot be negative");
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="Enter rate" />
                    </Form.Item>

                    {apiError && (
                        <Alert
                            message={apiError}
                            type="error"
                            showIcon
                            className="mb-3"
                        />
                    )}

                    <Button
                        type="primary"
                        disabled={!submittable}
                        loading={loading}
                        style={{ width: "100%" }}
                        onClick={handleRenew}
                    >
                        Update subscription plan
                    </Button>
                </Form>
            )}
        </Modal>
    );
};

export default UpdateTenantRateModal;